import { API_ENDPOINTS } from "./config";

const HOME_PAGE_API = API_ENDPOINTS.homePages;

let homePageRequest = null;

export const stripHtml = (html = "") => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
};

export const formatNewsDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const mapBannersToSlides = (banners = []) =>
  [...banners]
    .sort((a, b) => a.position - b.position)
    .map((banner) => {
      const plainDescription = stripHtml(banner.description);

      if (!banner.title?.trim() && banner.image_url) {
        return {
          id: banner.id,
          position: banner.position,
          type: "awards",
          image: banner.image_url,
        };
      }

      return {
        id: banner.id,
        position: banner.position,
        type: "content",
        subtitle: banner.title || "",
        title: plainDescription,
        image: banner.image_url,
      };
    });

export const mapWorkingProcesses = (items = []) =>
  [...items]
    .sort((a, b) => a.position - b.position)
    .map((item, index) => ({
      id: item.id,
      number: String(index + 1).padStart(2, "0"),
      title: item.title,
      description: item.description,
      variant: index === 1 ? "featured" : "light",
      to: index === 0 ? "/contact-us" : "/services",
    }));

export const mapTestimonials = (items = []) =>
  items.map((item) => ({
    id: item.id,
    quote: item.description,
    name: item.patient_name,
    role: "PATIENT",
    image: item.image,
  }));

export const mapExperiences = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) => ({
    id: item.id,
    value: item.value,
    title: item.title,
    description: item.description,
  }));

export const mapAbout = (item) => {
  if (!item) return null;

  const images = Array.isArray(item.images)
    ? item.images.filter(Boolean)
    : item.images
      ? [item.images]
      : [];

  const points = (Array.isArray(item.points) ? item.points : [])
    .map((point, index) => {
      if (typeof point === "string") {
        return { id: `point-${index}`, label: point, icon: null };
      }

      return {
        id: point.id || `point-${index}`,
        label: point.point || point.label || "",
        icon: point.logo || null,
      };
    })
    .filter((point) => point.label);

  const rawLegacy = Array.isArray(item.legacies) ? item.legacies[0] : null;
  const legacy = rawLegacy
    ? {
        id: rawLegacy.id,
        title: rawLegacy.title || "",
        description: rawLegacy.description || "",
        image: rawLegacy.image || null,
        points: [...(rawLegacy.legacy_points || [])]
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .map((point) => ({
            id: point.id,
            title: point.title || "",
            description: point.description || "",
          })),
      }
    : null;

  return {
    id: item.id,
    title: item.title || "",
    description: item.description || "",
    position: item.position,
    images,
    points,
    legacy,
  };
};

const SERVICE_STYLE_VARIANTS = [
  {
    cardBg: "from-[#FFF4EC] via-white to-white",
    border: "border-[#F5D0B5]",
    accent: "bg-brand-orange",
    iconBg: "bg-[#FFF0E6] border-[#F5CDB2]",
    arrowBg: "bg-brand-orange text-white border-brand-orange",
    badge: "bg-brand-orange",
  },
  {
    cardBg: "from-[#F6EFF9] via-white to-white",
    border: "border-[#DDD0E8]",
    accent: "bg-brand-purple",
    iconBg: "bg-[#F3EBF8] border-[#DDD0E8]",
    arrowBg: "bg-brand-purple text-white border-brand-purple",
    badge: "bg-brand-purple",
  },
];

export const mapServices = (items = []) =>
  [...items]
    .sort((a, b) => a.position - b.position)
    .map((item, index) => {
      const style = SERVICE_STYLE_VARIANTS[index % SERVICE_STYLE_VARIANTS.length];
      const remoteIcon = item.icon;
      const useRemoteIcon = remoteIcon && !/\.svg($|\?)/i.test(remoteIcon);

      return {
        id: item.id,
        number: String(index + 1).padStart(2, "0"),
        title: item.sort_title || item.title || "",
        description: item.sort_description || "",
        icon: useRemoteIcon ? remoteIcon : null,
        image: item.image || null,
        position: item.position,
        to: `/services/${item.id}`,
        ...style,
      };
    });

export const fetchHomePage = async () => {
  if (homePageRequest) return homePageRequest;

  homePageRequest = fetch(HOME_PAGE_API)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch home page data (${response.status})`);
      }
      return response.json();
    })
    .catch((error) => {
      homePageRequest = null;
      throw error;
    });

  return homePageRequest;
};
